const XENDIT_SCRIPT_URL = 'https://js.xendit.co/v1/xendit.min.js';

declare global {
	interface Window {
		Xendit?: any;
	}
}

let scriptPromise: Promise<void> | null = null;
let configuredKey: string | null = null;

function loadScript(): Promise<void> {
	if (window.Xendit) return Promise.resolve();
	if (scriptPromise) return scriptPromise;

	scriptPromise = new Promise<void>((resolve, reject) => {
		const script = document.createElement('script');
		script.src = XENDIT_SCRIPT_URL;
		script.async = true;
		script.onload = () => resolve();
		script.onerror = () => {
			scriptPromise = null;
			reject(new Error('Failed to load Xendit.js'));
		};
		document.head.appendChild(script);
	});

	return scriptPromise;
}

export async function ensureXendit(publicKey: string): Promise<void> {
	if (!publicKey) throw new Error('Missing Xendit public key');
	await loadScript();
	if (configuredKey !== publicKey) {
		window.Xendit!.setPublishableKey(publicKey);
		configuredKey = publicKey;
	}
}

export interface XenditCardToken {
	id: string;
	status: string;
	masked_card_number?: string;
	card_info?: {
		brand?: string;
		type?: string;
		bank?: string;
		country?: string;
	};
	failure_reason?: string;
	authentication_id?: string;
	payer_authentication_url?: string;
}

interface XenditAuthentication {
	id?: string;
	status?: string;
	failure_reason?: string;
	payer_authentication_url?: string;
}

const AUTH_OVERLAY_ID = 'xendit-3ds-overlay';

function openAuthOverlay(url: string): () => void {
	document.getElementById(AUTH_OVERLAY_ID)?.remove();

	const prevBodyOverflow = document.body.style.overflow;
	document.body.style.overflow = 'hidden';

	const backdrop = document.createElement('div');
	backdrop.id = AUTH_OVERLAY_ID;
	backdrop.style.cssText =
		'position:fixed;inset:0;z-index:2147483647;background:rgba(0,0,0,0.55);' +
		'display:flex;align-items:stretch;justify-content:center;padding:16px;box-sizing:border-box;';

	const card = document.createElement('div');
	card.style.cssText =
		'position:relative;width:min(480px,100%);height:100%;background:#fff;' +
		'border-radius:16px;overflow:hidden;box-shadow:0 12px 48px rgba(0,0,0,0.35);' +
		'display:flex;flex-direction:column;';

	const iframe = document.createElement('iframe');
	iframe.src = url;
	iframe.title = '3D Secure authentication';
	iframe.setAttribute('scrolling', 'no');
	iframe.style.cssText = 'flex:1 1 auto;width:100%;border:0;display:block;';

	card.appendChild(iframe);
	backdrop.appendChild(card);
	document.body.appendChild(backdrop);

	return () => {
		backdrop.remove();
		document.body.style.overflow = prevBodyOverflow;
	};
}


export interface CreateTokenParams {
	cardNumber: string;
	expMonth: string;
	expYear: string;
	cvn: string;
	// MUST be > 0, otherwise Xendit.js skips the 3DS challenge.
	amount: number;
	onBehalfOf?: string;
}

// Two-step legacy Cards 3DS: createToken (multi-use) vaults the PAN without a
// challenge; createAuthentication then runs the 3DS overlay.
export function createMultiUseCardToken(p: CreateTokenParams): Promise<XenditCardToken> {
	return new Promise<XenditCardToken>((resolve, reject) => {
		if (!(p.amount > 0)) {
			reject(new Error('Verification amount must be greater than zero for 3DS'));
			return;
		}

		const xenditCard = window.Xendit?.card;
		if (!xenditCard || typeof xenditCard.createAuthentication !== 'function') {
			reject(new Error('Xendit.js is missing card.createAuthentication; cannot run 3DS'));
			return;
		}

		const onBehalf = p.onBehalfOf ? { on_behalf_of: p.onBehalfOf } : {};

		const tokenData: Record<string, unknown> = {
			amount: p.amount,
			card_number: p.cardNumber,
			card_exp_month: p.expMonth,
			card_exp_year: p.expYear,
			card_cvn: p.cvn,
			is_multiple_use: true,
			should_authenticate: true,
			currency: 'PHP',
			...onBehalf,
		};

		xenditCard.createToken(tokenData, (err: any, token: XenditCardToken) => {
			if (err) {
				reject(new Error(err.message || 'Card tokenization failed'));
				return;
			}
			if (!token?.id) {
				reject(new Error('No token returned by Xendit'));
				return;
			}
			if (token.status === 'FAILED') {
				reject(new Error(token.failure_reason || 'Card verification failed'));
				return;
			}

			const authData: Record<string, unknown> = {
				token_id: token.id,
				amount: p.amount,
				currency: 'PHP',
				...onBehalf,
			};
			let closeOverlay: (() => void) | null = null;

			xenditCard.createAuthentication(authData, (authErr: any, authentication: XenditAuthentication) => {
				if (authErr) {
					closeOverlay?.();
					reject(new Error(authErr.message || '3DS authentication failed'));
					return;
				}
				if (!authentication) {
					closeOverlay?.();
					reject(new Error('No authentication returned by Xendit'));
					return;
				}

				switch (authentication.status) {
					case 'VERIFIED':
						closeOverlay?.();
						resolve({ ...token, status: 'VERIFIED', authentication_id: authentication.id });
						break;
					case 'FAILED':
						closeOverlay?.();
						reject(new Error(authentication.failure_reason || 'Card 3DS authentication failed'));
						break;
					default:
						if (authentication.payer_authentication_url && !closeOverlay) {
							closeOverlay = openAuthOverlay(authentication.payer_authentication_url);
						}
						break;
				}
			});
		});
	});
}
